/* eslint-disable max-len */
export interface EmailTemplateData {
  customerFirstName: string;
  orderNumber: string;
  reviewLink: string;
  email: string;
  fullAddress: string;
  productNames: string;
}

type LanguageCode = 'en-GB' | 'en-US' | 'de' | 'fr' | 'it' | 'es'

export function getConfirmationEmailTemplate(data: EmailTemplateData, language = 'en-GB'): { subject: string; body: string } {
  const templates: Record<LanguageCode, { subject: string; body: string }> = {
    'en-GB': {
      subject: '🎨 Thanks for your review – your Colozoo gift is on its way!',
      body: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #333;">
          <h2 style="color: #ff6b35;">🎨 Thanks for your review – your Colozoo gift is on its way!</h2>
          
          <p>Hi ${data.customerFirstName},</p>
          
          <p>Thank you so much for your review and for being part of the Colozoo community! We've received your free gift request and we're already working on it.</p>
          
          <p>Here's a quick summary of what you sent us:</p>
          
          <div style="background-color: #f9f9f9; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #ff6b35; margin-top: 0;">📦 Order Details:</h3>
            <ul style="list-style: none; padding-left: 0;">
              <li>• Order number: <strong>${data.orderNumber}</strong></li>
              <li>• Review link: <a href="${data.reviewLink}" style="color: #ff6b35;">${data.reviewLink}</a></li>
              <li>• Email: <strong>${data.email}</strong></li>
              <li>• Shipping address:<br/><strong>${data.fullAddress}</strong></li>
            </ul>
            
            <h3 style="color: #ff6b35;">🎁 Gift Choice:</h3>
            <p><strong>${data.productNames}</strong> (up to €25)</p>
          </div>
          
          <p>We'll check your details and prepare your gift for dispatch. It usually takes up to 7 days. You'll get a notification once it's on the way.</p>
          
          <p>If the product you chose isn't available or something's unclear, we'll get in touch via email.</p>
          
          <div style="background-color: #fff3e0; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <p style="margin: 0;"><strong>🧡 Just one more thing:</strong><br/>
            If you've had any issues with your product, please contact us directly — we're always happy to help 🐵✉️</p>
          </div>
          
          <p>Thanks again for supporting a small, colourful brand. Happy creating!</p>
          
          <p style="margin-top: 30px;">
            Warm wishes,<br/>
            <strong>The Colozoo Team</strong><br/>
            🌐 <a href="https://www.colozoo.de" style="color: #ff6b35;">www.colozoo.de</a><br/>
            📸 Instagram: <a href="https://instagram.com/colozoo.official" style="color: #ff6b35;">@colozoo.official</a>
          </p>
        </div>
      `
    },
    'en-US': {
      subject: '🎨 Thanks for your review – your Colozoo gift is on the way!',
      body: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #333;">
          <h2 style="color: #ff6b35;">🎨 Thanks for your review – your Colozoo gift is on the way!</h2>
          
          <p>Hi ${data.customerFirstName},</p>
          
          <p>Thank you so much for your review and for being part of the Colozoo community! We've received your free gift request and we're already getting it ready.</p>
          
          <p>Here's a quick summary of what you submitted:</p>
          
          <div style="background-color: #f9f9f9; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #ff6b35; margin-top: 0;">📦 Order Details:</h3>
            <ul style="list-style: none; padding-left: 0;">
              <li>• Order number: <strong>${data.orderNumber}</strong></li>
              <li>• Review link: <a href="${data.reviewLink}" style="color: #ff6b35;">${data.reviewLink}</a></li>
              <li>• Email: <strong>${data.email}</strong></li>
              <li>• Shipping address:<br/><strong>${data.fullAddress}</strong></li>
            </ul>
            
            <h3 style="color: #ff6b35;">🎁 Gift Choice:</h3>
            <p><strong>${data.productNames}</strong> (up to €25)</p>
          </div>
          
          <p>We'll check everything and get your gift shipped out. It usually takes up to 7 days. We'll notify you once it's on the way.</p>
          
          <p>If your chosen product is out of stock or anything's unclear, we'll reach out by email.</p>
          
          <div style="background-color: #fff3e0; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <p style="margin: 0;"><strong>🧡 One last thing:</strong><br/>
            If you had any issues with your Colozoo product, feel free to message us directly — we're here to help 🐵✉️</p>
          </div>
          
          <p>Thanks again for supporting a small, colorful brand. Happy creating!</p>
          
          <p style="margin-top: 30px;">
            Warm regards,<br/>
            <strong>The Colozoo Team</strong><br/>
            🌐 <a href="https://www.colozoo.de" style="color: #ff6b35;">www.colozoo.de</a><br/>
            📸 Instagram: <a href="https://instagram.com/colozoo.official" style="color: #ff6b35;">@colozoo.official</a>
          </p>
        </div>
      `
    },
    'de': {
      subject: '🎨 Danke für deine Bewertung – dein Colozoo-Geschenk ist unterwegs!',
      body: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #333;">
          <h2 style="color: #ff6b35;">🎨 Danke für deine Bewertung – dein Colozoo-Geschenk ist unterwegs!</h2>
          
          <p>Hallo ${data.customerFirstName},</p>
          
          <p>vielen Dank für deine Bewertung und dein Vertrauen in Colozoo! Wir haben deine Anfrage für das Gratisprodukt erhalten und kümmern uns bereits darum.</p>
          
          <p>Hier eine kurze Zusammenfassung deiner Angaben:</p>
          
          <div style="background-color: #f9f9f9; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #ff6b35; margin-top: 0;">📦 Bestelldetails:</h3>
            <ul style="list-style: none; padding-left: 0;">
              <li>• Bestellnummer: <strong>${data.orderNumber}</strong></li>
              <li>• Link zur Bewertung: <a href="${data.reviewLink}" style="color: #ff6b35;">${data.reviewLink}</a></li>
              <li>• E-Mail: <strong>${data.email}</strong></li>
              <li>• Lieferadresse:<br/><strong>${data.fullAddress}</strong></li>
            </ul>
            
            <h3 style="color: #ff6b35;">🎁 Wunschprodukt:</h3>
            <p><strong>${data.productNames}</strong> (bis 25 €)</p>
          </div>
          
          <p>Wir prüfen alles und bereiten den Versand deines Geschenks vor. Das dauert normalerweise bis zu 7 Tage. Du bekommst eine Nachricht, sobald es unterwegs ist.</p>
          
          <p>Sollte dein Wunschprodukt nicht verfügbar sein oder es Unklarheiten geben, 
          melden wir uns per E-Mail bei dir.</p>
          
          <div style="background-color: #fff3e0; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <p style="margin: 0;"><strong>🧡 Noch ein Hinweis:</strong><br/>
            Wenn du ein Problem mit deinem Produkt hattest, schreib uns bitte direkt – wir helfen dir gerne weiter 🐵✉️</p>
          </div>
          
          <p>Vielen Dank, dass du unsere kleine, bunte Marke unterstützt. Viel Spaß beim Kreativsein!</p>
          
          <p style="margin-top: 30px;">
            Herzliche Grüße,<br/>
            <strong>Dein Colozoo-Team</strong><br/>
            🌐 <a href="https://www.colozoo.de" style="color: #ff6b35;">www.colozoo.de</a><br/>
            📸 Instagram: <a href="https://instagram.com/colozoo.official" style="color: #ff6b35;">@colozoo.official</a>
          </p>
        </div>
      `
    },
    'fr': {
      subject: '🎨 Merci pour votre avis – votre cadeau Colozoo est en route !',
      body: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #333;">
          <h2 style="color: #ff6b35;">🎨 Merci pour votre avis – votre cadeau Colozoo est en route !</h2>
          
          <p>Bonjour ${data.customerFirstName},</p>
          
          <p>Merci beaucoup pour votre avis et pour faire partie de la communauté Colozoo ! Nous avons bien reçu votre demande de cadeau gratuit et nous nous en occupons déjà.</p>
          
          <p>Voici un petit récapitulatif :</p>
          
          <div style="background-color: #f9f9f9; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #ff6b35; margin-top: 0;">📦 Détails de commande :</h3>
            <ul style="list-style: none; padding-left: 0;">
              <li>• Numéro de commande : <strong>${data.orderNumber}</strong></li>
              <li>• Lien vers l'avis : <a href="${data.reviewLink}" style="color: #ff6b35;">${data.reviewLink}</a></li>
              <li>• E-mail : <strong>${data.email}</strong></li>
              <li>• Adresse de livraison :<br/><strong>${data.fullAddress}</strong></li>
            </ul>
            
            <h3 style="color: #ff6b35;">🎁 Cadeau choisi :</h3>
            <p><strong>${data.productNames}</strong> (jusqu'à 25 €)</p>
          </div>
          
          <p>Nous vérifierons les informations et préparerons l'envoi. Cela prend généralement jusqu'à 7 jours. Vous recevrez un message quand le colis sera en route.</p>
          
          <p>Si le produit est en rupture ou qu'une info manque, nous vous écrirons par e-mail.</p>
          
          <div style="background-color: #fff3e0; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <p style="margin: 0;"><strong>🧡 Une dernière chose :</strong><br/>
            Si vous avez rencontré un souci, n'hésitez pas à nous écrire directement – on est là pour vous aider 🐵✉️</p>
          </div>
          
          <p>Merci encore de soutenir une petite marque haute en couleurs. Amusez-vous bien avec vos créations !</p>
          
          <p style="margin-top: 30px;">
            À très vite,<br/>
            <strong>L'équipe Colozoo</strong><br/>
            🌐 <a href="https://www.colozoo.de" style="color: #ff6b35;">www.colozoo.de</a><br/>
            📸 Instagram : <a href="https://instagram.com/colozoo.official" style="color: #ff6b35;">@colozoo.official</a>
          </p>
        </div>
      `
    },
    'it': {
      subject: '🎨 Grazie per la tua recensione – Il tuo regalo Colozoo sta arrivando!',
      body: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #333;">
          <h2 style="color: #ff6b35;">🎨 Grazie per la tua recensione – Il tuo regalo Colozoo sta arrivando!</h2>
          
          <p>Ciao ${data.customerFirstName},</p>
          
          <p>Grazie mille per la tua recensione e per far parte della community Colozoo! Abbiamo ricevuto la tua richiesta per un prodotto omaggio e ci stiamo già lavorando.</p>
          
          <p>Ecco un riepilogo dei dati che ci hai inviato:</p>
          
          <div style="background-color: #f9f9f9; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #ff6b35; margin-top: 0;">📦 Dettagli ordine:</h3>
            <ul style="list-style: none; padding-left: 0;">
              <li>• Numero ordine: <strong>${data.orderNumber}</strong></li>
              <li>• Link alla recensione: <a href="${data.reviewLink}" style="color: #ff6b35;">${data.reviewLink}</a></li>
              <li>• Email: <strong>${data.email}</strong></li>
              <li>• Indirizzo di spedizione:<br/><strong>${data.fullAddress}</strong></li>
            </ul>
            
            <h3 style="color: #ff6b35;">🎁 Prodotto scelto:</h3>
            <p><strong>${data.productNames}</strong> (max €25)</p>
          </div>
          
          <p>Verificheremo tutto e prepareremo la spedizione. Di solito richiede fino a 7 giorni. 
          Ti avviseremo appena parte.</p>
          
          <p>Se il prodotto scelto non è disponibile o c'è qualcosa di poco chiaro, ti contatteremo via email.</p>
          
          <div style="background-color: #fff3e0; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <p style="margin: 0;"><strong>🧡 Solo un'ultima cosa:</strong><br/>
            Se hai avuto un problema con il prodotto, scrivici direttamente – siamo qui per aiutarti 🐵✉️</p>
          </div>
          
          <p>Grazie ancora per sostenere un piccolo brand colorato. Buon divertimento creativo!</p>
          
          <p style="margin-top: 30px;">
            Un caro saluto,<br/>
            <strong>Il team di Colozoo</strong><br/>
            🌐 <a href="https://www.colozoo.de" style="color: #ff6b35;">www.colozoo.de</a><br/>
            📸 Instagram: <a href="https://instagram.com/colozoo.official" style="color: #ff6b35;">@colozoo.official</a>
          </p>
        </div>
      `
    },
    'es': {
      subject: '🎨 ¡Gracias por tu reseña – Tu regalo de Colozoo va en camino!',
      body: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #333;">
          <h2 style="color: #ff6b35;">🎨 ¡Gracias por tu reseña – Tu regalo de Colozoo va en camino!</h2>
          
          <p>Hola ${data.customerFirstName},</p>
          
          <p>¡Muchas gracias por tu reseña y por confiar en Colozoo! Hemos recibido tu solicitud del regalo gratuito y ya estamos trabajando en ello.</p>
          
          <p>Aquí tienes un resumen de tu información:</p>
          
          <div style="background-color: #f9f9f9; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #ff6b35; margin-top: 0;">📦 Detalles del pedido:</h3>
            <ul style="list-style: none; padding-left: 0;">
              <li>• Nº de pedido: <strong>${data.orderNumber}</strong></li>
              <li>• Enlace a la reseña: <a href="${data.reviewLink}" style="color: #ff6b35;">${data.reviewLink}</a></li>
              <li>• Email: <strong>${data.email}</strong></li>
              <li>• Dirección de envío:<br/><strong>${data.fullAddress}</strong></li>
            </ul>
            
            <h3 style="color: #ff6b35;">🎁 Producto elegido:</h3>
            <p><strong>${data.productNames}</strong> (hasta 25 €)</p>
          </div>
          
          <p>Comprobaremos todos los datos y prepararemos el envío. Suele tardar hasta 7 días. 
          Te avisaremos cuando salga.</p>
          
          <p>Si el producto no está disponible o hay dudas, te escribiremos por email.</p>
          
          <div style="background-color: #fff3e0; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <p style="margin: 0;"><strong>🧡 Solo una cosa más:</strong><br/>
            Si tuviste algún problema con tu producto, escríbenos directamente — ¡estamos aquí para ayudarte! 🐵✉️</p>
          </div>
          
          <p>Gracias por apoyar a una marca pequeña y colorida. ¡Felices creaciones!</p>
          
          <p style="margin-top: 30px;">
            Un abrazo,<br/>
            <strong>El equipo de Colozoo</strong><br/>
            🌐 <a href="https://www.colozoo.de" style="color: #ff6b35;">www.colozoo.de</a><br/>
            📸 Instagram: <a href="https://instagram.com/colozoo.official" style="color: #ff6b35;">@colozoo.official</a>
          </p>
        </div>
      `
    }  }

  // Default to English if language not found
  const template = templates[language as LanguageCode] || templates['en-GB']
  return template
}

// Helper function to extract first name from full name or email
export function extractFirstName(nameOrEmail: string): string {
  if (!nameOrEmail) return 'Friend'
  
  // If it's an email, try to extract name from the part before @
  if (nameOrEmail.includes('@')) {
    const emailName = nameOrEmail.split('@')[0]
    // Remove dots, underscores, numbers and capitalize
    const cleanName = emailName.replace(/[._0-9]/g, ' ').trim()
    if (cleanName) {
      return cleanName.split(' ')[0].charAt(0).toUpperCase() + cleanName.split(' ')[0].slice(1).toLowerCase()
    }
  }
  
  // If it's a name, return the first part
  const firstName = nameOrEmail.split(' ')[0]
  return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
}

// Helper function to detect language from country or email domain
export function detectLanguage(country: string, email: string): string {
  const countryLowerCase = country.toLowerCase()
  const emailDomain = email.toLowerCase()
  
  console.log('detectLanguage called with:', { country, email, countryLowerCase, emailDomain })
  
  // Map countries to languages
  if (countryLowerCase.includes('germany') || countryLowerCase.includes('deutschland') || 
      countryLowerCase.includes('austria') || countryLowerCase.includes('österreich') ||
      countryLowerCase.includes('switzerland') || countryLowerCase.includes('schweiz') ||
      emailDomain.includes('.de') || emailDomain.includes('.at') || emailDomain.includes('.ch')) {
    console.log('Detected German')
    return 'de'
  }
  
  if (countryLowerCase.includes('france') || countryLowerCase.includes('belgium') || 
      countryLowerCase.includes('luxembourg') || emailDomain.includes('.fr') || 
      emailDomain.includes('.be') || emailDomain.includes('.lu')) {
    console.log('Detected French')
    return 'fr'
  }
  
  if (countryLowerCase.includes('italy') || countryLowerCase.includes('italia') || 
      emailDomain.includes('.it')) {
    console.log('Detected Italian')
    return 'it'
  }  if (countryLowerCase.includes('spain') || countryLowerCase.includes('españa') || 
      countryLowerCase.includes('mexico') || countryLowerCase.includes('argentina') ||
      countryLowerCase.includes('colombia') || emailDomain.endsWith('.es') || 
      emailDomain.endsWith('.mx') || emailDomain.endsWith('.co') || emailDomain.endsWith('.ar')) {
    console.log('Detected Spanish')
    return 'es'
  }
  
  // Check for US-specific indicators
  if (countryLowerCase.includes('united states') || countryLowerCase.includes('usa') || 
      countryLowerCase.includes('america') || emailDomain.includes('.us')) {
    console.log('Detected US English')
    return 'en-US'
  }
  
  // Default to British English for UK and other English-speaking countries
  if (countryLowerCase.includes('united kingdom') || countryLowerCase.includes('england') || 
      countryLowerCase.includes('scotland') || countryLowerCase.includes('wales') || 
      countryLowerCase.includes('ireland') || countryLowerCase.includes('canada') || 
      countryLowerCase.includes('australia') || countryLowerCase.includes('new zealand') ||
      emailDomain.includes('.uk') || emailDomain.includes('.ca') || emailDomain.includes('.au') || 
      emailDomain.includes('.nz')) {
    console.log('Detected British English')
    return 'en-GB'
  }
  
  // Default to British English
  console.log('Defaulted to British English')
  return 'en-GB'
}
